import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { parseNumericValue } from '@/services/pmt2Utils';
// Import Timestamp and Firestore functions
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase'; // Import db instance
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { LinkIcon } from 'lucide-react'; // Import LinkIcon
// Import service functions
import { fetchLatestOldValuesMap, fetchLatestLedgerEntryDate } from '@/services/pmt3OldValuesService';

interface MarginDashboardProps {
  product: PMT2Product | null;
}

type OldValuesMap = Record<string, string>;

// Helper function to fetch a specific URL from the client URL database
const fetchClientUrl = async (companyName: string, docId: string): Promise<string | null> => {
    if (!companyName || !docId) return null;
    try {
        // Sanitize docId before using it in the path
        const sanitizedDocId = docId.replace(/\//g, '_').replace(/\./g, '_');
        const urlDocRef = doc(db, 'pmt2_clienturldatabase', companyName, 'items', sanitizedDocId);
        const urlDocSnap = await getDoc(urlDocRef);
        return urlDocSnap.exists() ? urlDocSnap.data()?.value ?? null : null;
    } catch (error) {
        console.error(`Error fetching client URL for ${docId}:`, error);
        return null;
    }
};


const MarginDashboard: React.FC<MarginDashboardProps> = ({ product }) => {
  const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null);
  const [grossProfitOverrideDate, setGrossProfitOverrideDate] = useState<Timestamp | null>(null);
  const [bufferOverrideDate, setBufferOverrideDate] = useState<Timestamp | null>(null);
  const [acosOverrideDate, setAcosOverrideDate] = useState<Timestamp | null>(null);
  // State for URLs
  const [priceUpdaterUrl, setPriceUpdaterUrl] = useState<string | null>(null);
  const [marginHistoryUrl, setMarginHistoryUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // --- Formatting & Utility Helpers ---
  const formatCurrency = (value: any, decimals = 2): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  const formatDecimalAsPercentage = (value: any): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return `${(num * 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`;
  };

  const formatDefault = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };

  const calculateDaysAgo = (timestamp: Timestamp | null | undefined): number | null => {
      if (!timestamp || !(timestamp instanceof Timestamp)) return null;
      const now = new Date();
      const pastDate = timestamp.toDate();
      const diffTime = now.getTime() - pastDate.getTime();
      if (diffTime < 0) return 0;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
  };

  const excelSerialDateToJSDate = (serial: number | null | undefined): Date | null => {
      if (serial === null || serial === undefined || isNaN(serial) || serial < 1) return null;
      const excelEpochDiff = 25567;
      const daysSinceJSEpoch = serial - excelEpochDiff;
      const milliseconds = daysSinceJSEpoch * 24 * 60 * 60 * 1000;
      return new Date(milliseconds);
  };

  const formatDateMMDDYYYY = (date: Date | null): string => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '-';
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = date.getUTCDate().toString().padStart(2, '0');
      const year = date.getUTCFullYear();
      return `${month}/${day}/${year}`;
  };
  // --- End Helpers ---

  // Fetch Old Values Map, Override Dates, and URLs
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      // Reset all fetched state
      setOldValuesMap(null);
      setGrossProfitOverrideDate(null);
      setBufferOverrideDate(null);
      setAcosOverrideDate(null);
      setPriceUpdaterUrl(null);
      setMarginHistoryUrl(null);

      if (product?.companyName && product?.id) {
        const company = product.companyName;
        const asin = product.id;
        const priceUpdaterDocId = '98_ Price Updater'; // Use sanitized ID if necessary
        const marginHistoryDocId = 'Margin History';

        try {
          // Fetch all data concurrently
          const results = await Promise.allSettled([
            fetchLatestOldValuesMap(company, asin),
            fetchLatestLedgerEntryDate(company, asin, 'Gross Profit Override'),
            fetchLatestLedgerEntryDate(company, asin, 'Final Gross Profit Buffer %Override'),
            fetchLatestLedgerEntryDate(company, asin, 'Final Target ACoSOverride'),
            fetchClientUrl(company, priceUpdaterDocId), // Fetch Price Updater URL
            fetchClientUrl(company, marginHistoryDocId) // Fetch Margin History URL
          ]);

          // Process results
          if (results[0].status === 'fulfilled') setOldValuesMap(results[0].value);
          else console.error("Error fetching old values map:", results[0].reason);

          if (results[1].status === 'fulfilled') setGrossProfitOverrideDate(results[1].value);
          else console.error("Error fetching Gross Profit Override date:", results[1].reason);

          if (results[2].status === 'fulfilled') setBufferOverrideDate(results[2].value);
          else console.error("Error fetching Buffer Override date:", results[2].reason);

          if (results[3].status === 'fulfilled') setAcosOverrideDate(results[3].value);
          else console.error("Error fetching ACoS Override date:", results[3].reason);

          if (results[4].status === 'fulfilled') setPriceUpdaterUrl(results[4].value);
          else console.error("Error fetching Price Updater URL:", results[4].reason);

          if (results[5].status === 'fulfilled') setMarginHistoryUrl(results[5].value);
          else console.error("Error fetching Margin History URL:", results[5].reason);

        } catch (error) {
          console.error("Error fetching Margin dashboard data:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [product]);

  // Configuration for dashboard items
  const marginItemsConfig = [
    { label: 'Price', valueKey: 'Price', oldValueKey: 'Price', format: 'currency', linkUrl: priceUpdaterUrl }, // Add linkUrl
    { label: 'COGS', valueKey: 'COGS', oldValueKey: 'COGS', format: 'currency' },
    { label: 'Referral Fee %', valueKey: 'Referral Fee %', oldValueKey: 'Referral Fee %', format: 'percent' },
    { label: 'FBA Fee', valueKey: 'FBA Fee', oldValueKey: 'FBA Fee', format: 'currency' },
    { label: 'Final Gross Profit Buffer %', valueKey: 'Final Gross Profit Buffer %', oldValueKey: 'Final Gross Profit Buffer %', overrideValueKey: 'Final Gross Profit Buffer %Override', oldOverrideValueKey: 'Final Gross Profit Buffer %Override', format: 'percent', overrideDateStateKey: 'bufferOverrideDate' },
    { label: 'Gross Profit', valueKey: 'Gross Profit', oldValueKey: 'Gross Profit', overrideValueKey: 'Gross Profit Override', oldOverrideValueKey: 'Gross ProfitOverride', format: 'currency', overrideDateStateKey: 'grossProfitOverrideDate' },
    { label: 'Gross Profit Margin', valueKey: 'Gross Profit Margin', oldValueKey: 'Gross Profit Margin', format: 'percent' }, // No override
    { label: 'Final Target ACoS', valueKey: 'Final Target ACoS', oldValueKey: 'Final Target ACoS', overrideValueKey: 'Final Target ACoSOverride', oldOverrideValueKey: 'Final Target ACoSOverride', format: 'percent', overrideDateStateKey: 'acosOverrideDate' },
  ];

  // Helper to format display values based on config
  const formatDisplay = (value: any, formatType?: string): string => {
      if (value === null || value === undefined || value === '') return '-';
      switch (formatType) {
          case 'currency':
              return formatCurrency(value);
          case 'percent':
              return formatDecimalAsPercentage(value);
          default:
              return formatDefault(value);
      }
  };


  if (!product) {
       return (
            <Card>
                <CardHeader><CardTitle className="text-2xl font-bold">Margin</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-500">Select a product to view Margin details.</p></CardContent>
            </Card>
        );
  }

  // Map state keys to actual state variables
  const overrideDates: { [key: string]: Timestamp | null } = {
      grossProfitOverrideDate,
      bufferOverrideDate,
      acosOverrideDate,
  };

  return (
    <Card className="max-w-4xl mx-auto">
      {/* Removed flex classes from CardHeader */}
      <CardHeader className="p-4">
        {/* Wrap Title and Icon Link in a flex container */}
        <div className="flex items-center gap-2">
            <CardTitle className="text-2xl font-bold">Margin</CardTitle>
            {marginHistoryUrl && (
                 <a
                    href={marginHistoryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Margin History Sheet"
                    className="text-blue-500 hover:text-blue-700 inline-flex items-center justify-center" // Added inline-flex for alignment
                 >
                    <LinkIcon size={18} />
                 </a>
            )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-1/4">Item</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Old Value</TableHead>
                    <TableHead className="text-right">Override</TableHead>
                    <TableHead className="text-right">Old Override</TableHead>
                    <TableHead className="text-right">Override Days Ago</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {marginItemsConfig.map((item) => {
                    const currentValue = product[item.valueKey];
                    const displayValue = formatDisplay(currentValue, item.format);

                    const oldValue = oldValuesMap?.[item.oldValueKey];
                    const formattedOldValue = formatDisplay(oldValue, item.format);
                    const displayOldValue = formattedOldValue !== '-' && displayValue !== formattedOldValue;

                    const currentOverrideValue = item.overrideValueKey ? product[item.overrideValueKey] : undefined;
                    const displayOverrideValue = formatDisplay(currentOverrideValue, item.format);
                    const showCurrentOverrideHighlight = item.overrideValueKey && displayOverrideValue !== '-';

                    const oldOverrideValue = item.oldOverrideValueKey ? oldValuesMap?.[item.oldOverrideValueKey] : undefined;
                    const formattedOldOverrideValue = formatDisplay(oldOverrideValue, item.format);
                    const highlightOldOverride = formattedOldOverrideValue !== '-' && displayOverrideValue !== formattedOldOverrideValue;
                    const showOldOverride = formattedOldOverrideValue !== '-';

                    const overrideTimestamp = item.overrideDateStateKey ? overrideDates[item.overrideDateStateKey] : null;
                    const daysAgo = calculateDaysAgo(overrideTimestamp);
                    const overrideExists = currentOverrideValue !== null && currentOverrideValue !== undefined && currentOverrideValue !== '' && currentOverrideValue !== '-';
                    const displayDaysAgo = overrideExists && daysAgo !== null ? `${daysAgo} days` : '-';

                    // Determine if the label should be a link
                    const isPriceLink = item.label === 'Price' && item.linkUrl;

                    return (
                        <TableRow key={item.label} className={item.label === 'Final Gross Profit Buffer %' ? 'border-b border-gray-200 dark:border-gray-700' : ''}>
                            <TableCell className="font-medium py-1 px-2">
                                {isPriceLink ? (
                                    <a
                                        href={item.linkUrl ?? '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline"
                                        title="Price Updater Sheet"
                                    >
                                        {item.label}
                                    </a>
                                ) : (
                                    item.label
                                )}
                            </TableCell>
                            <TableCell className={`text-right py-1 px-2 ${item.label === 'Gross Profit' ? 'font-bold' : ''} ${
                                item.label === 'Gross Profit' && !isLoading ? (
                                    parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! > 0
                                        ? 'text-green-700 dark:text-green-500'
                                        : parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! < 0
                                            ? 'text-red-600 dark:text-red-500'
                                            : ''
                                ) : ''
                            }`}>
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : displayValue}
                            </TableCell>
                            <TableCell className="text-right py-1 px-2">
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    displayOldValue ? (
                                        <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                            {formattedOldValue}
                                        </span>
                                    ) : (
                                        <span>-</span>
                                    )
                                )}
                            </TableCell>
                            <TableCell className="text-right py-1 px-2">
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    item.overrideValueKey && showCurrentOverrideHighlight ? (
                                         <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                             {displayOverrideValue}
                                         </span>
                                     ) : (
                                         // Remove '-' for specific rows in Override column
                                         ['Price', 'COGS', 'Referral Fee %', 'FBA Fee', 'Gross Profit Margin'].includes(item.label) && displayOverrideValue === '-' ? '' : <span>{item.overrideValueKey ? displayOverrideValue : '-'}</span>
                                     )
                                )}
                            </TableCell>
                             <TableCell className="text-right py-1 px-2">
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    item.oldOverrideValueKey && showOldOverride ? (
                                        <span className={highlightOldOverride ? "bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100" : ""}>
                                            {formattedOldOverrideValue}
                                        </span>
                                    ) : (
                                        // Remove '-' for specific rows in Old Override column
                                        ['Price', 'COGS', 'Referral Fee %', 'FBA Fee', 'Gross Profit Margin'].includes(item.label) && formattedOldOverrideValue === '-' ? '' : <span>-</span>
                                    )
                                )}
                            </TableCell>
                             <TableCell className="text-right py-1 px-2">
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    // Remove '-' for specific rows in Override Days Ago column
                                    ['Price', 'COGS', 'Referral Fee %', 'FBA Fee', 'Gross Profit Margin'].includes(item.label) && displayDaysAgo === '-' ? '' : (item.overrideValueKey ? displayDaysAgo : '-')
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default MarginDashboard;